import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useMerchant } from '../context/MerchantContext';
import { useCart } from '../context/CartContext';
import {
  getSalesRecoveryNotificationPermission,
  getSalesRecoveryPushSetupIssue,
  requestBrowserRecoveryNotifications
} from '../services/salesRecoveryService';

const DISMISSED_KEY = 'sales-recovery-notification-banner-dismissed';

const PromptShell = styled.div`
  position: fixed;
  left: 50%;
  bottom: calc(76px + env(safe-area-inset-bottom));
  z-index: 999999;
  width: min(calc(100vw - 24px), 460px);
  transform: translateX(-50%);
  background: #172033;
  color: #fff;
  border-radius: 8px;
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.24);
  padding: 0.95rem;
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.08);

  @media (min-width: 992px) {
    bottom: 24px;
  }
`;

const IconWrap = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #f4c542;
  color: #172033;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const PromptText = styled.div`
  min-width: 0;

  strong {
    display: block;
    font-size: 0.96rem;
    line-height: 1.2;
  }

  span {
    display: block;
    color: #e5e7eb;
    font-size: 0.8rem;
    line-height: 1.3;
    margin-top: 0.18rem;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;

const EnableButton = styled.button`
  border: 0;
  border-radius: 8px;
  background: #f4c542;
  color: #172033;
  font-size: 0.82rem;
  font-weight: 800;
  min-height: 36px;
  padding: 0 0.95rem;
  cursor: pointer;

  &:disabled {
    cursor: wait;
    opacity: 0.72;
  }
`;

const CloseButton = styled.button`
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #d1d5db;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
`;

const SalesRecoveryNotificationPrompt: React.FC = () => {
  const { merchantId, restaurant } = useMerchant();
  const { state: cartState } = useCart();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [setupIssue, setSetupIssue] = useState<string | null>(null);
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>('unsupported');

  const currentMerchantId = merchantId || restaurant?.id;
  const hasCartItems = cartState.items.length > 0;

  useEffect(() => {
    if (!currentMerchantId || !hasCartItems) {
      setVisible(false);
      return;
    }

    const currentPermission = getSalesRecoveryNotificationPermission();
    const currentSetupIssue = getSalesRecoveryPushSetupIssue();
    const dismissed = localStorage.getItem(DISMISSED_KEY) === 'true';

    setPermission(currentPermission);
    setSetupIssue(currentSetupIssue);
    setVisible(currentPermission !== 'granted' && !dismissed);
  }, [currentMerchantId, hasCartItems]);

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    setVisible(false);
  };

  const enableNotifications = async () => {
    if (!currentMerchantId) return;

    try {
      setLoading(true);
      const currentSetupIssue = getSalesRecoveryPushSetupIssue();
      if (currentSetupIssue) {
        setSetupIssue(currentSetupIssue);
        toast.error(currentSetupIssue);
        return;
      }

      const result = await requestBrowserRecoveryNotifications(currentMerchantId, true);
      const currentPermission = getSalesRecoveryNotificationPermission();
      setPermission(currentPermission);

      if (result || currentPermission === 'granted') {
        toast.success('Notificações ativadas');
        setVisible(false);
        return;
      }

      if (currentPermission === 'denied') {
        toast.error('Notificações bloqueadas no navegador. Libere nas configurações do site.');
        return;
      }

      toast.error('As notificações não foram habilitadas no navegador.');
    } catch (error) {
      console.error('[SalesRecovery] Could not enable browser notifications:', error);
      toast.error(error instanceof Error ? error.message : 'Não foi possível ativar notificações');
    } finally {
      setLoading(false);
    }
  };

  if (!visible) return null;

  const isBlocked = permission === 'denied';
  const isUnavailable = permission === 'unsupported' || permission === 'denied' || !!setupIssue;

  return (
    <PromptShell role="dialog" aria-live="polite" aria-label="Ativar notificações">
      <IconWrap>
        <span aria-hidden="true">%</span>
      </IconWrap>
      <PromptText>
        <strong>
          {isBlocked ? 'Notificações bloqueadas' : 'Receba promoções e cupons de desconto'}
        </strong>
        <span>
          {isBlocked
            ? 'Libere as notificações nas configurações do navegador para receber ofertas da loja.'
            : isUnavailable
              ? setupIssue || 'Este navegador não permite receber notificações.'
              : 'Ative os avisos para não perder ofertas e recuperar seu carrinho com benefícios.'}
        </span>
      </PromptText>
      <Actions>
        {!isUnavailable && (
          <EnableButton type="button" onClick={enableNotifications} disabled={loading}>
            {loading ? '...' : 'Quero receber'}
          </EnableButton>
        )}
        <CloseButton type="button" onClick={dismiss} aria-label="Fechar">
          x
        </CloseButton>
      </Actions>
    </PromptShell>
  );
};

export default SalesRecoveryNotificationPrompt;
